/**
 * V2 My lists — every list you own, plus the create form (M8).
 *
 * The list-of-lists view stays thin on purpose: tapping a list opens its
 * page (/user/{you}/list/{slug}), and that page carries all the owner
 * tools — rename, visibility, notes, reorder, delete. "Want to go"
 * appears here once it exists (first save creates it).
 */
import { escapeHtml } from '../../../assets/js/utils.js';
import { icon } from '../../ui/icons.js';
import { showToast } from '../../ui/toast.js';
import { fetchMyLists, fetchFollowedLists, createList, normalizeUrl } from './lists.js';
import { loadCategoryField, categoryFieldHtml, resolveCategoryId } from './category-field.js';

const VISIBILITY_LABEL = {
  private: 'Private — only you',
  unlisted: 'Unlisted — anyone with the link',
  public: 'Public — shown on your journal',
};

function listRow(list) {
  const places = `${list.entry_count} place${list.entry_count === 1 ? '' : 's'}`;
  return `
    <button type="button" class="mylist-row" data-open-list="${escapeHtml(list.slug)}">
      ${icon('bookmark', 20)}
      <span class="mylist-body">
        <span class="mylist-title">${list.featured ? '<span class="featured-star" title="Picked by the rangers" aria-label="Featured">★</span> ' : ''}${escapeHtml(list.title)}</span>
        <span class="mylist-meta">${places} · ${escapeHtml(VISIBILITY_LABEL[list.visibility] || list.visibility)}</span>
      </span>
    </button>`;
}

// A followed list opens on its owner's page; the byline says whose it is.
function followedRow(list) {
  const who = list.owner.full_name || list.owner.username;
  const places = `${list.entry_count} place${list.entry_count === 1 ? '' : 's'}`;
  return `
    <button type="button" class="mylist-row" data-open-followed
      data-owner="${escapeHtml(list.owner.username)}" data-slug="${escapeHtml(list.slug)}">
      ${icon('bookmark', 20)}
      <span class="mylist-body">
        <span class="mylist-title">${list.featured ? '<span class="featured-star" aria-label="Featured">★</span> ' : ''}${escapeHtml(list.title)}</span>
        <span class="mylist-meta">by ${escapeHtml(who)} · ${places}</span>
      </span>
    </button>`;
}

function render(el, lists, followed, basePath, categoryCats, busy = false) {
  el.innerHTML = `
    <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
    <section class="v2-mylists" aria-label="My lists">
      <h2 class="v2-page-title">My lists</h2>
      <div class="mylist-rows">
        ${lists.map(listRow).join('') ||
          '<p class="v2-empty">No lists yet. Saving a place as “Want to go” starts one, or make your own below.</p>'}
      </div>
      ${followed?.length ? `
      <h3 class="mylist-new-title mylist-followed-title">Lists you follow</h3>
      <div class="mylist-rows">
        ${followed.map(followedRow).join('')}
      </div>` : ''}
      <form class="mylist-new" data-new-list-form>
        <h3 class="mylist-new-title">Start a new list</h3>
        <input type="text" name="title" aria-label="List name"
          placeholder="List name (e.g. Top ten burgers)" maxlength="80" required>
        <textarea name="description" aria-label="What’s this list about?"
          placeholder="What’s this list about? (optional)" rows="2" maxlength="500"></textarea>
        <input type="url" name="url" aria-label="Link for this list"
          placeholder="Link (optional) — Instagram, TikTok, your blog…" maxlength="500" autocomplete="off">
        ${categoryFieldHtml(categoryCats, null, '-new')}
        <label class="mylist-vis-label">Who can see it?
          <select name="visibility">
            <option value="private">${VISIBILITY_LABEL.private}</option>
            <option value="unlisted">${VISIBILITY_LABEL.unlisted}</option>
            <option value="public">${VISIBILITY_LABEL.public}</option>
          </select>
        </label>
        <button type="submit" class="v2-btn-leather" ${busy ? 'disabled' : ''}>Create list</button>
      </form>
    </section>`;
}

export function mount(el, ctx) {
  const { store, router } = ctx;
  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';
  let destroyed = false;
  let lists = [];
  let followed = null; // null = table not applied yet → section stays hidden
  let categoryCats = null; // null = column not applied yet → field stays hidden

  async function load() {
    try {
      [lists, followed, categoryCats] = await Promise.all([
        fetchMyLists(store),
        fetchFollowedLists(store), // resolves null on failure, never throws
        loadCategoryField(), // resolves null pre-migration, never throws
      ]);
      if (!destroyed) render(el, lists, followed, basePath, categoryCats);
    } catch {
      if (!destroyed) {
        el.innerHTML = `
          <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
          <div class="v2-empty">Lists aren’t available right now.</div>`;
      }
    }
  }

  function start() {
    if (!store.get().user) {
      if (!store.get().authReady) {
        const unsub = store.subscribe((s) => {
          if (!s.authReady || destroyed) return;
          unsub();
          start();
        });
        el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
        return;
      }
      el.innerHTML = `
        <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
        <div class="v2-empty">Sign in (top right) to keep lists of places.</div>`;
      return;
    }
    el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
    load();
  }

  async function onSubmit(event) {
    const form = event.target.closest('[data-new-list-form]');
    if (!form) return;
    event.preventDefault();
    const fields = form.elements;
    const title = fields.title.value.trim();
    if (!title) return;
    const categoryId = categoryCats ? resolveCategoryId(categoryCats, fields.category?.value) : null;
    if (categoryId === undefined) {
      showToast('That category didn’t match — pick one from the suggestions, or leave it blank.');
      return;
    }
    render(el, lists, followed, basePath, categoryCats, true);
    try {
      await createList(store, {
        title,
        description: fields.description?.value.trim() || null,
        visibility: fields.visibility?.value || 'private',
        url: normalizeUrl(fields.url?.value),
        categoryId,
      });
      showToast('List created.', { type: 'success' });
      await load();
    } catch (err) {
      showToast(err?.message || 'Couldn’t create that list.');
      await load();
    }
  }

  // The Import and On-the-way doors left this page 2026-08-05: both live
  // elsewhere now (Import your places on your journal + Help; On the way
  // in Discover's Explore drawer), and a list page is for lists.
  function onClick(event) {
    const followedRowEl = event.target.closest('[data-open-followed]');
    if (followedRowEl) {
      router.navigate('list', {
        username: followedRowEl.dataset.owner,
        slug: followedRowEl.dataset.slug,
      });
      return;
    }
    const row = event.target.closest('[data-open-list]');
    if (!row) return;
    const username = store.get().profile?.username;
    if (username) router.navigate('list', { username, slug: row.dataset.openList });
  }

  el.addEventListener('submit', onSubmit);
  el.addEventListener('click', onClick);
  start();

  return () => {
    destroyed = true;
    el.removeEventListener('submit', onSubmit);
    el.removeEventListener('click', onClick);
  };
}
